import React, { useContext, useMemo } from "react";
import { AppText, FlexDiv } from "src/Components/UI";
import { PhoenixIcon } from "src/Components/UI/Phoenix";
import { ModalContext } from "src/context";
import { edit, list } from "src/images/NewDesign";
import { theme } from "src/utils/theme";
import styled from "styled-components";

interface IMoreInfoTab {
  leadData: any;
  maxHeight?: number;
}

export const MoreInfoTab: React.FC<IMoreInfoTab> = ({ leadData, maxHeight }) => {
  const { setCustomFieldsData, openCustomFieldsModal } = useContext(ModalContext);

  const fields = useMemo(() => {
    return leadData?.visible_custom_fields?.filter((field: any) => field?.computed_value) || [];
  }, [leadData]);

  return (
    <Wrapper maxHeight={maxHeight}>
      <Container>
        <FlexDiv
          align="center"
          gap={4}
          style={{ marginLeft: "auto", cursor: "pointer" }}
          onClick={() => {
            setCustomFieldsData({
              lead: {
                id: leadData?.id,
                visible_custom_fields: leadData?.visible_custom_fields,
              },
            });
            openCustomFieldsModal();
          }}
        >
          <PhoenixIcon
            svg={edit}
            size={16}
            color={theme.icon.brand.default}
            hoverColor={theme.icon.brand.default}
            pointer
          />
          <AppText
            fontSize={10}
            fontWeight={600}
            letterSpacing={1}
            uppercase
            color={theme.buttontext.brand_outline.default}
          >
            Edit More Info
          </AppText>
        </FlexDiv>

        {!fields.length ? (
          <FlexDiv direction="column" gap={24} align="center" padding="48px 0px 0px 0px">
            <PhoenixIcon
              svg={list}
              size={40}
              color={theme.icon.neutral.default}
              hoverColor={theme.icon.neutral.default}
            />
            <AppText fontSize={14} fontWeight={400} lineHeight={22} color={theme.text.neutral.tertiary}>
              No custom data for this lead
            </AppText>
          </FlexDiv>
        ) : (
          <FieldWrapper>
            {fields.map((field: any, index: number) => {
              const fieldValue = field?.computed_value;
              const value = fieldValue || field?.value;
              const fullWidth = fields.length % 2 !== 0 && index === fields.length - 1;

              return (
                <Field key={`${leadData?.id}-${field.key}`} label={field.key} value={value} fullWidth={fullWidth} />
              );
            })}
          </FieldWrapper>
        )}
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ maxHeight?: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;

  height: 100%;
  max-height: ${({ maxHeight }) => (maxHeight ? `${maxHeight}px` : "100%")};

  overflow-y: auto;
`;

const Container = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 16px;

  width: 70%;
  max-width: 784px;
  min-width: 330px;

  padding: 16px 0px;
`;

export const FieldWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;

  width: 100%;
  height: fit-content;
  padding: 8px;

  border: 1px solid ${theme.border.neutral.primary};
  border-radius: 8px;

  background-color: ${theme.fill.neutral.primary};
`;

export const Field: React.FC<{ label: string; value: string; fullWidth: boolean }> = ({ label, value, fullWidth }) => {
  const safeUrl = useMemo(() => {
    if (typeof value !== "string") return null;

    let trimmed = value.trim();

    const linkTest = new RegExp(
      "([a-zA-Z0-9]+://)?([a-zA-Z0-9_]+:[a-zA-Z0-9_]+@)?([a-zA-Z0-9.-]+\\.[A-Za-z]{2,4})(:[0-9]+)?(/.*)?",
    ).test(trimmed);
    if (!linkTest) return null;

    // Add http if no scheme exists
    const hasProtocol = /^[a-z][a-z0-9+\-.]*:\/\//i.test(trimmed);
    if (!hasProtocol) {
      trimmed = "http://" + trimmed;
    }

    try {
      const url = new URL(trimmed);
      const allowedSchemes = ["http:", "https:", "mailto:"];
      if (!allowedSchemes.includes(url.protocol)) return null;
      return url.href;
    } catch {
      return null;
    }
  }, [value]);

  const isLink = Boolean(safeUrl);

  return (
    <FieldContainer fullWidth={fullWidth}>
      <AppText fontSize={10} fontWeight={400} lineHeight={14} color={theme.text.neutral.secondary}>
        {label}
      </AppText>
      <AppText
        fontSize={12}
        fontWeight={400}
        lineHeight={18}
        color={isLink ? theme.text.brand.primary : theme.text.neutral.primary}
        onClick={() => {
          if (safeUrl) {
            window.open(safeUrl, "_blank", "noopener,noreferrer");
          }
        }}
        style={{
          borderBottom: "none",
          cursor: isLink ? "pointer" : "initial",
          maxWidth: "100%",
          wordWrap: "break-word",
        }}
      >
        {value}
      </AppText>
    </FieldContainer>
  );
};

const FieldContainer = styled.div<{ fullWidth: boolean }>`
  display: flex;
  flex-direction: column;

  width: ${(props) => (props.fullWidth ? "100%" : "50%")};

  padding: 4px 8px;

  border-bottom: 1px solid ${theme.border.neutral.secondary};
`;
